import { Plan } from '@otter-ai/recall-client';
import { PurchaseSetInitialPlanEvent, trackEvent } from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { isEduAtom, planTypeAtom } from '#atoms';
import { plansQueryCreator } from '#query';

const isValidPurchasePlan = (plan: Plan, selectedPlanType: string) => {
  return (
    plan.is_team &&
    plan.type === selectedPlanType &&
    plan.discount_type !== 'academic'
  );
};

export const usePurchasePlanOptions = () => {
  const isEdu = useAtomValue(isEduAtom);
  const planType = useAtomValue(planTypeAtom);
  const searchParams = useSearchParams();

  const { data } = useQuery(plansQueryCreator());
  const hParam = searchParams.get('h'); // 'month' or 'year'

  const activePlans = useMemo(() => {
    return data?.filter((plan: Plan) => plan.stripe_plan_id !== null);
  }, [data]);

  const { annualPlan, monthlyPlan } = useMemo(() => {
    const planOptions = activePlans?.filter((plan: Plan) => {
      if (isEdu) {
        return plan.type === 'premium' && plan.discount_type === 'academic';
      }
      if (planType) {
        return isValidPurchasePlan(plan, planType);
      }
      return false;
    });

    return {
      annualPlan: planOptions?.find(
        (plan: Plan) => plan.payment_period_type === 'year',
      ),
      monthlyPlan: planOptions?.find(
        (plan: Plan) => plan.payment_period_type === 'month',
      ),
    };
  }, [activePlans, isEdu, planType]);

  const initialPlan = useMemo(() => {
    let initialPlan = annualPlan;
    if (hParam === 'month') {
      initialPlan = monthlyPlan;
    }
    trackEvent(
      PurchaseSetInitialPlanEvent({
        PlanID: initialPlan?.id,
      }),
    );
    return initialPlan;
  }, [hParam, annualPlan, monthlyPlan]);

  return {
    annualPlan,
    monthlyPlan,
    initialPlan,
  };
};
